import { useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, ArrowLeft, Info, UserPlus, ChevronRight, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { createRegisterSchema } from '@/schemas/registerSchema.ts';
import type { RegisterFormValues } from '@/schemas/registerSchema.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FormError from '@/components/shared/FormError.tsx';
import AvatarCropperModal from '@/components/profile/AvatarCropperModal.tsx';
import authService from '@/services/authService.ts';

export default function RegisterForm() {
  const { t } = useTranslation();
  const { register: registerUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Destino elegido en la home MVP (p. ej. /evaluate o /pitch) antes de registrarse.
  const next = (location.state as { next?: string } | null)?.next;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const schema = useMemo(() => createRegisterSchema(t), [t]);

  // Optional profile photo (cropped client-side, uploaded after the account exists).
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(schema),
  });

  const onAvatarSave = (blob: Blob) => {
    setAvatarBlob(blob);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(blob);
    });
    setPendingFile(null);
  };

  const onSubmit = async (data: RegisterFormValues) => {
    setSubmitError(null);
    try {
      const user = await registerUser(data.email, data.password, data.firstName, data.lastName, data.phone, data.termsAccepted);
      // Optional photo: account exists now (token stored) → upload, then refresh.
      if (avatarBlob) {
        try {
          await authService.uploadAvatar(avatarBlob);
          await refreshUser();
        } catch { /* non-blocking — user can add it later from the profile menu */ }
      }
      if (next) {
        navigate(next);
      } else if (user.role === 'SuperUser') {
        // SuperUser tiene su propia landing minimal en /dashboard.
        navigate('/dashboard');
      } else {
        // MVP: el usuario normal va a su flujo elegido (backend), o a la home.
        navigate(user.chosenFlow ? `/${user.chosenFlow}` : '/');
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('auth.registerError'));
    }
  };

  return (
    <div className="w-full max-w-md">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors no-underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('auth.backLink')}
      </Link>
      <div className="rounded-2xl border border-border bg-card p-8 shadow-sm transition-shadow duration-200 hover:shadow-xl sm:p-10">
        {/* Cabecera al estilo de las tarjetas de la home (icono gradiente Agencium) */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-2xl text-white"
            style={{ background: 'var(--gradient-agencium)' }}
          >
            <UserPlus className="size-7" />
          </div>
          <h1 className="text-xl font-bold text-foreground sm:text-2xl">
            {t('auth.registerTitle')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('auth.registerSubtitle')}
          </p>
        </div>

        {/* Optional profile photo (facoltativa) */}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) setPendingFile(f);
          }}
        />
        <div className="mb-7 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="group relative h-20 w-20 overflow-hidden rounded-full border border-border bg-muted text-muted-foreground transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('avatar.add', 'Carica foto')}
          >
            {avatarPreview ? (
              <img src={avatarPreview} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center">
                <Camera className="size-7" />
              </span>
            )}
            <span className="absolute inset-x-0 bottom-0 bg-black/45 py-0.5 text-center text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
              {avatarPreview ? t('avatar.change', 'Cambia') : t('avatar.add', 'Carica')}
            </span>
          </button>
          <span className="text-xs text-muted-foreground">
            {t('avatar.optionalHint', 'Aggiungi una foto (facoltativa)')}
          </span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* First name + Last name */}
          <div>
          {/* Mini-help: el scoring premia presentarse con nombre y apellido reales */}
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-primary/5 px-3 py-2.5">
            <Info className="mt-0.5 size-4 shrink-0 text-primary" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t('auth.fullNameHelp')}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="firstName"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                {t('auth.firstNameLabel')}
              </label>
              <input
                id="firstName"
                type="text"
                placeholder={t('auth.firstNamePlaceholder')}
                autoComplete="given-name"
                className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.firstName.message}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                {t('auth.lastNameLabel')}
              </label>
              <input
                id="lastName"
                type="text"
                placeholder={t('auth.lastNamePlaceholder')}
                autoComplete="family-name"
                className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.lastName.message}
                </p>
              )}
            </div>
          </div>
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.emailLabel')}
            </label>
            <input
              id="email"
              type="email"
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label
              htmlFor="phone"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.phoneLabel')}
            </label>
            <input
              id="phone"
              type="tel"
              placeholder={t('auth.phonePlaceholder')}
              autoComplete="tel"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('phone')}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-destructive">
                {errors.phone.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.passwordLabel')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordHint')}
                autoComplete="new-password"
                className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                {...register('password')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                tabIndex={-1}
                aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.confirmPasswordLabel')}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                autoComplete="new-password"
                className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                {...register('confirmPassword')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                tabIndex={-1}
                aria-label={showConfirmPassword ? t('common.hidePassword') : t('common.showPassword')}
              >
                {showConfirmPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-destructive">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Terms acceptance */}
          <div>
            <label className="flex items-start gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                {...register('termsAccepted')}
              />
              <span>
                {t('auth.termsAcceptPrefix', 'He leído y acepto la')}{' '}
                <Link
                  to="/data-privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary hover:text-primary-hover no-underline"
                >
                  {t('auth.termsAcceptLink', 'política de privacidad')}
                </Link>
                {t('auth.termsAcceptSuffix', ' y el tratamiento de mis datos personales descritos en ella.')}
              </span>
            </label>
            {errors.termsAccepted && (
              <p className="mt-1 text-xs text-destructive">
                {errors.termsAccepted.message}
              </p>
            )}
          </div>

          <FormError
            message={submitError}
            onDismiss={() => setSubmitError(null)}
          />

          {/* Submit */}
          <Button type="submit" size="lg" disabled={isSubmitting} className="group w-full">
            {isSubmitting ? t('auth.registerSubmitting') : t('auth.registerSubmit')}
            <ChevronRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t('auth.hasAccount')}{' '}
          <Link
            to="/login"
            state={next ? { next } : undefined}
            className="font-medium text-primary hover:text-primary-hover transition-colors no-underline"
          >
            {t('auth.loginLink')}
          </Link>
        </p>
      </div>

      <AvatarCropperModal
        file={pendingFile}
        onCancel={() => setPendingFile(null)}
        onSave={onAvatarSave}
      />
    </div>
  );
}
